import React, { useState } from 'react';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { PageSteps } from '@common/components';
import { useMutation, useNavigate } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import { APPLY_BULK_WORKFORCE_GROUP_SHIFT_GAP_RATE_OVERRIDES_MUTATION } from '@scheduling/graphql/mutations/bulk-shift-incentive-override';
import { BulkShiftStepSelectShifts } from './_bulk-shift-step-select-shifts';
import { BulkShiftStepSetTiers } from './_bulk-shift-step-set-tiers';
import { BulkShiftStepConfirm } from './_bulk-shift-step-confirm';
import {
  buildApplyOverridesPayload,
  hasAnyValidSelection,
} from './_bulk-shift-step-set-tiers-service';
import type {
  SelectedGapEntryType,
  Step2SelectionsType,
} from './_bulk-shift-step-types';

export const SHIFTS_PAGE_PATH = '/works/shifts/feed';

export function BulkShiftIncentiveOverrideSteps() {
  const navigate = useNavigate();
  const [selectedGaps, setSelectedGaps] = useState<SelectedGapEntryType[]>([]);
  const [selections, setSelections] = useState<Step2SelectionsType>({});

  const [applyOverrides, { loading: applying }] = useMutation(
    APPLY_BULK_WORKFORCE_GROUP_SHIFT_GAP_RATE_OVERRIDES_MUTATION,
    {
      onCompleted: () => {
        createMessage('success', 'Bulk shift rate overrides applied.');
        goToShiftsPage();
      },
      onError: () => {
        createMessage(
          'error',
          'Unable to apply bulk overrides. Please try again.'
        );
      },
    }
  );

  useSetBreadcrumbs([
    { name: 'Shifts', path: SHIFTS_PAGE_PATH },
    {
      name: 'Override Shift Rates',
      path: `${SHIFTS_PAGE_PATH}/bulk-override-rates`,
    },
  ]);

  function goToShiftsPage() {
    navigate(SHIFTS_PAGE_PATH);
  }

  // Drop selections that no longer have a matching selected gap so an empty
  // Step 1 always resets Step 2 too.
  function handleSelectionChange(next: SelectedGapEntryType[]) {
    setSelectedGaps(next);
    if (next.length === 0) setSelections({});
  }

  function handleApply() {
    const payload = buildApplyOverridesPayload(selections);

    if (payload.length === 0 || selectedGaps.length === 0) {
      createMessage(
        'error',
        'Pick at least one workforce-group rate to apply.'
      );
      return;
    }

    void applyOverrides({
      variables: {
        shiftGapIds: selectedGaps.map((gap) => gap.shiftGapId),
        overrides: payload,
      },
    });
  }

  const canApply = hasAnyValidSelection(selections);

  return (
    <PageSteps
      steps={[
        {
          title: 'Select Shifts',
          page: (
            <BulkShiftStepSelectShifts
              selectedGaps={selectedGaps}
              onSelectionChange={handleSelectionChange}
            />
          ),
          forwardButtonDisabled: selectedGaps.length === 0,
        },
        {
          title: 'Set Rates',
          page: (
            <BulkShiftStepSetTiers
              selectedGaps={selectedGaps}
              selections={selections}
              onSelectionsChange={setSelections}
            />
          ),
          forwardButtonDisabled: !canApply,
        },
        {
          title: 'Confirm Rate Overrides',
          confirmButtonText: 'Apply overrides',
          page: (
            <BulkShiftStepConfirm
              selectedGaps={selectedGaps}
              selections={selections}
            />
          ),
          forwardButtonDisabled: !canApply,
        },
      ]}
      confirmLoading={applying}
      onConfirm={handleApply}
    />
  );
}
